import React, { type ReactNode } from 'react'
import { css } from '@emotion/core'
import PropTypes from 'prop-types'
import { Ellipsis, usePageWidth } from '@fs/zion-ui'
import { VisuallyHidden } from '@reach/visually-hidden'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { getLanguageName } from '@fs/zion-rootstech'
import { FLAGS } from '../config'
import { useFlagsContext } from '../providers'
import { formatDuration, getEnv } from './helpers'

export type BadgeColor = 'yellow' | 'green' | 'blue' | 'teal' | 'red' | 'purple' | 'gray' | 'danger'

interface BadgeProps {
  label: string | ReactNode
  color?: BadgeColor
  solid?: boolean
  keyline?: string
  maxWidth?: string | number
  'aria-label'?: string
  [key: string]: unknown
}

interface DurationBadgeProps {
  duration: string | number
  customCss?: Record<string, string | number>
}

interface SessionDifficultyBadgeProps {
  sessionDifficulty: string
}

// Fixed RootsTech day values: the zion night tokens would flip these, but the design keeps solid badges the same in both themes.
const SOLID_COLORS: Record<BadgeColor, string> = {
  yellow: '#af4517',
  green: '#307535',
  blue: '#006f98',
  teal: '#007562',
  red: '#bd2383',
  purple: '#8e40c9',
  gray: '#333435',
  danger: '#b53f35',
}
const SOLID_TEXT_COLOR = '#fefefe'

const badgeCss = css`
  display: flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 3px 5px;
  &[key-line='left'] {
    padding-inline-start: 0px;
  }
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  line-height: normal;
  background-color: var(--background-color);
  color: var(--color);
`

const getColorVars = (color: BadgeColor | undefined, solid: boolean): Record<string, string> => {
  if (!color) return { '--background-color': 'transparent', '--border-color': 'transparent', '--color': 'inherit' }
  if (solid) {
    return {
      '--background-color': SOLID_COLORS[color],
      '--border-color': SOLID_COLORS[color],
      '--color': SOLID_TEXT_COLOR,
    }
  }
  return {
    '--background-color': `var(--${color}05a)`,
    '--border-color': `var(--${color}20a)`,
    '--color': `var(--${color}90a)`,
  }
}

const getLabelText = (label: string | ReactNode): string | ReactNode => {
  if (typeof label === 'string') return label
  if (label && typeof label === 'object' && 'props' in label) {
    return label.props?.children || label
  }
  return label
}

export const Badge = ({
  label,
  color,
  solid = false,
  keyline,
  maxWidth,
  'aria-label': ariaLabel,
  ...props
}: BadgeProps): JSX.Element => {
  return (
    <div
      css={badgeCss}
      style={{ ...getColorVars(color, solid), maxWidth: maxWidth ?? 'fit-content' } as React.CSSProperties}
      {...props}
      key-line={keyline}
    >
      <Ellipsis aria-hidden="true">{label}</Ellipsis>
      <VisuallyHidden>{ariaLabel || getLabelText(label)}</VisuallyHidden>
    </div>
  )
}

Badge.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  color: PropTypes.oneOf(Object.keys(SOLID_COLORS)),
  solid: PropTypes.bool,
}

export const DurationBadge = ({ duration, customCss }: DurationBadgeProps): JSX.Element | null => {
  const [t] = useTranslation()
  const formattedDuration = formatDuration(duration)
  const [secondsStr = '0', minutesStr = '0', hoursStr = '0'] = String(formattedDuration)?.split(':').reverse() || []
  const seconds = Number(secondsStr)
  const minutes = Number(minutesStr)
  const hours = Number(hoursStr)
  const durationIsZero = Number(String(formattedDuration)?.replaceAll(':', '')) === 0

  if (durationIsZero) return null

  const prettyDuration = `${hours > 0 ? `${hours}:` : ''}`
    .concat(`${String(minutes).padStart(2, '0')}:`)
    .concat(`${String(seconds).padStart(2, '0')}`)
  return (
    <Badge
      color="gray"
      solid
      label={<time dateTime={`PT${hours}H${minutes}M${seconds}S`}>{prettyDuration}</time>}
      data-testid="badge-duration"
      css={customCss}
      aria-label={t('badge.duration.label', 'Video duration is {duration}', { duration: prettyDuration })}
    />
  )
}

export const SeriesBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="teal"
      label={t('card.tag.series', 'Series')}
      data-testid="badge-series"
      aria-label={t('badge.series', 'This session is part of a series')}
    />
  )
}

export const LanguageBadge = ({ contentLocale }: { contentLocale?: string }): JSX.Element | null => {
  const [t] = useTranslation()
  const userLocale = i18n.language
  const contentIsInUsersLanguage = contentLocale?.split('-')[0] === userLocale
  const atWidth = usePageWidth()
  if (!contentLocale || contentIsInUsersLanguage || contentLocale === 'und') return null

  const translatedName = getLanguageName(contentLocale.split('-')[0])

  return (
    <Badge
      color="gray"
      solid
      label={translatedName}
      data-testid={`badge-language-${contentLocale}`}
      aria-label={t('badge.language', 'This session language is {translatedName}', { translatedName })}
      maxWidth={atWidth({
        default: 90,
        xxs: 112,
        xs: 120,
        md: 80,
        lg: 112,
        xxl: 136,
      })}
    />
  )
}

export const LiveBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="danger"
      solid
      label={t('card.tag.live', 'Live')}
      data-testid="badge-live"
      aria-label={t('badge.live', 'This session is live')}
    />
  )
}
export const ReplayBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="danger"
      solid
      label={t('card.tag.replay', 'Replay')}
      data-testid="badge-replay"
      aria-label={t('badge.replay', 'This session is a replay')}
    />
  )
}

export const DraftBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="yellow"
      label={t('card.tag.draft', 'Draft')}
      data-testid="badge-draft"
      aria-label={t('badge.draft', 'This session is in draft')}
    />
  )
}

export const SubmittedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="green"
      label={t('card.tag.submitted', 'Submitted')}
      data-testid="badge-submitted"
      aria-label={t('badge.submitted', 'This session is submitted')}
    />
  )
}

export const EditableBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="yellow"
      label={t('card.tag.editable', 'In Progress')}
      data-testid="badge-editable"
      aria-label={t('badge.progress', 'This session is in progress')}
    />
  )
}

export const AcceptedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="teal"
      label={t('card.tag.accepted', 'Accepted')}
      data-testid="badge-accepted"
      aria-label={t('badge.accepted', 'This session has been accepted')}
    />
  )
}

export const PublishedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="blue"
      label={t('card.tag.published', 'Published')}
      data-testid="badge-published"
      aria-label={t('badge.published', 'This session is published')}
    />
  )
}

export const PendingBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge color="blue" label={t('card.tag.pending', 'Pending')} data-testid="badge-pending" />
}

export const ApprovedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge color="green" label={t('card.tag.approved', 'Approved')} data-testid="badge-approved" />
}

export const DeclinedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge color="red" label={t('card.tag.declined', 'Declined')} data-testid="badge-declined" />
}

export const RevokedBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge color="red" label={t('card.tag.revoked', 'Revoked')} data-testid="badge-revoked" />
}

export const PromotionBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge color="gray" label={t('card.tag.promotion', 'Promotion')} data-testid="badge-promotion" />
}

export const PDFBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return <Badge color="gray" label={t('card.tag.pdf', 'PDF')} data-testid="badge-pdf" />
}

export const ComingSoonBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="gray"
      solid
      label={t('badge.coming-soon.label', 'Coming Soon')}
      aria-label={t('badge.coming-soon.label', 'Coming Soon')}
      data-testid="coming-soon-badge"
    />
  )
}

export const OnlineBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="green"
      label={t('upload-content.session-location.online.label', 'Online')}
      data-testid="badge-online"
      aria-label={t('badge.online', 'This session is online')}
    />
  )
}

export const InPersonBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="yellow"
      label={t('upload-content.session-location.in-person.label', 'In person')}
      data-testid="badge-inperson"
      aria-label={t('badge.in-person', 'This session is in person')}
    />
  )
}

export const InPersonOnlyBadge = (): JSX.Element => {
  const [t] = useTranslation()
  return (
    <Badge
      color="yellow"
      label={t('upload-content.session-location.in-person-only.label', 'This class is not available online')}
      data-testid="badge-inpersononly"
      aria-label={t('badge.in-person-only', 'This session is in person only')}
    />
  )
}

export const SessionDifficultyBadge = ({ sessionDifficulty }: SessionDifficultyBadgeProps): JSX.Element | null => {
  const [t] = useTranslation()

  const difficulties: Record<string, { color: BadgeColor; label: string }> = {
    Beginner: { color: 'blue', label: t('card.session-difficulty.beginner.label', 'Beginner') },
    Intermediate: { color: 'red', label: t('card.session-difficulty.intermediate.label', 'Intermediate') },
    'Advanced/Professional': {
      color: 'purple',
      label: t('card.session-difficulty.advanced.label', 'Advanced/Professional'),
    },
    All: { color: 'gray', label: t('card.session-difficulty.all.label', 'All Levels') },
  }
  const difficulty = difficulties[sessionDifficulty]

  if (!difficulty) {
    return null
  }

  const difficultyLabel = difficulty.label

  return (
    <Badge
      color={difficulty.color}
      label={difficultyLabel}
      data-testid="badge-difficulty"
      aria-label={t('badge.difficulty', 'This session has a skill level of {difficultyLabel}', { difficultyLabel })}
    />
  )
}

interface VideoProcessingStatusBadgeProps {
  bucket: string
}

export const VideoProcessingStatusBadge = ({ bucket }: VideoProcessingStatusBadgeProps): JSX.Element | null => {
  const [t] = useTranslation()

  const buckets: Record<string, { color: BadgeColor; label: string }> = {
    NOT_STARTED: {
      color: 'gray',
      label: t('dashboard.video-processing.status.not-started', 'Not Started'),
    },
    PENDING: { color: 'blue', label: t('dashboard.video-processing.status.pending', 'Pending') },
    IN_PROGRESS: {
      color: 'yellow',
      label: t('dashboard.video-processing.status.in-progress', 'In Progress'),
    },
    COMPLETED: {
      color: 'green',
      label: t('dashboard.video-processing.status.completed', 'Completed'),
    },
    ERROR: { color: 'red', label: t('dashboard.video-processing.status.error', 'Error') },
  }

  if (!buckets[bucket]) {
    return null
  }

  return (
    <Badge
      color={buckets[bucket].color}
      label={buckets[bucket].label}
      data-testid={`badge-video-processing-${bucket.toLowerCase()}`}
    />
  )
}

export const EnvironmentBadge = (): JSX.Element | null => {
  const { [FLAGS.PREVIEW]: previewFlag } = useFlagsContext()
  const environments: Record<string, { color: BadgeColor; label: string }> = {
    integration: { color: 'yellow', label: 'Integration' },
    beta: { color: 'gray', label: 'Beta' },
    // www: // NOTE: we don't want to show this is prod
  }
  const env = getEnv()

  if (!environments[env] && !previewFlag.isOn) {
    return null
  }

  return (
    <>
      {previewFlag.isOn && <Badge color="yellow" label="Conference Preview" data-testid="badge-preview" />}
      {environments[env] && (
        <Badge color={environments[env].color} solid label={environments[env].label} data-testid="badge-environment" />
      )}
    </>
  )
}
